// src/components/modals/AssignCourseTeacherModal.jsx
//
// Lets a supplier pick which of their team teachers (see
// SupplierTeachers.jsx's roster) teaches a given course. Includes an
// explicit "no teacher yet" option since courses.teacher_id is
// nullable and a supplier-created course often starts that way.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

export default function AssignCourseTeacherModal({ isOpen, onClose, course, teachers, loading, onSubmit }) {
  const { showAlert } = useUser();
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected(course?.teacher?.id ?? null);
  }, [isOpen, course]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !course) return null;

  const currentId = course?.teacher?.id ?? null;
  const unchanged = selected === currentId;
  const team = teachers || [];

  const handleSave = async () => {
    if (unchanged) return onClose?.();
    setSubmitting(true);
    try {
      await onSubmit?.(selected);
      onClose?.();
    } catch (err) {
      showAlert?.(err?.message || 'שיוך המורה נכשל', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose} />
      {submitting && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div
        className="relative z-[1] w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20 flex flex-col max-h-[85dvh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5.5 20a6.5 6.5 0 0 1 13 0" strokeLinecap="round" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base leading-tight">שיוך מורה</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{course.title}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-5 sm:px-6 py-4 space-y-2 overflow-y-auto">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-3 text-right transition ${
              selected === null ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span
              className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                selected === null ? 'border-indigo-500' : 'border-gray-300'
              }`}
            >
              {selected === null && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
            </span>
            <span className="min-w-0">
              <span className={`block text-sm font-semibold ${selected === null ? 'text-indigo-700' : 'text-gray-800'}`}>
                ללא מורה משויך
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">לשיוך מאוחר יותר</span>
            </span>
          </button>

          {loading ? (
            <SpinnerOverlay variant="glow" text="טוען מורים..." dark={false} blur={false} />
          ) : team.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">
              לא נמצאו מורים מאושרים במאגר.
            </div>
          ) : (
            team.map((t) => {
              const active = t.id === selected;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-3 text-right transition ${
                    active ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      active ? 'border-indigo-500' : 'border-gray-300'
                    }`}
                  >
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                  </span>

                  {t.avatar ? (
                    <img
                      src={t.avatar}
                      alt={`${t.firstName} ${t.lastName}`}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-100"
                      onError={(e) => {
                        // Broken/expired photo URL — fall back to the
                        // letter circle instead of a broken-image icon.
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span
                    className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold items-center justify-center flex-shrink-0"
                    style={{ display: t.avatar ? 'none' : 'flex' }}
                  >
                    {t.firstName?.charAt(0)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-800'}`}>
                      {t.firstName} {t.lastName}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">{(t.subjects || []).join(', ')}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm px-5 sm:px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-40"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || unchanged}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold shadow-md shadow-indigo-200 hover:shadow-lg hover:-translate-y-px transition-all duration-150 disabled:opacity-40 disabled:translate-y-0 disabled:shadow-none"
          >
            {submitting ? 'שומר…' : 'שמור שיוך'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}